/**
 * G-36 数值变化反馈层：数字滚动 + 涨跌脉冲 + 飘字
 *
 * 面板刷新走的是 textContent 直写，数值从 100 跳到 115 是「换了一帧静态图」，
 * 玩家得回头读结算文本才知道自己涨了什么。这里把「值变了」这件事转成一组动效：
 *   ④ 数字滚动（rAF 补间）② 脉冲（复用 ui.css 已有的 pulse-ok / pulse-ng）① 飘字（.delta-float）
 *
 * 四条硬约束：
 *   1. 只在「此刻玩家看得见」时播放。面板 display:none、或被月度执行弹窗的模糊遮罩压住时，
 *      文字照样落定但【不动基线】——等下一次真正可见的 refresh 一次性回放整段涨跌
 *      （月末结算的累积变化正是在返回决策页那一刻才第一次进入视野）。
 *   2. prefers-reduced-motion 下不播、也不欠一段「回头补播」的动画。
 *   3. 宿主元素必须 position: relative（飘字挂自身），且必须是块级盒
 *      （flex item / grid item 会自动块化；真 inline span 的 transform 会被忽略，脉冲＝看不见）。
 *   4. 基线按「局」作废：面板是进程内单例，「再来一局」复用同一批节点，
 *      不 bump 局号就会让新开局从上一位角色的值滚过来（见 resetFeedback()，由 app.onNewGame 调用）。
 *      同一局之内的开局链条也要 bump：建角确认时才挂开局天赋（富家千金 setMoney），
 *      晚于决策页已落好的首写基线，不重置就会把「开局给了多少钱」回放成一次成长（app.ts / CharacterCreatePanel.confirm 两处调用）。
 *      判据统一为一句话：这段差值玩家有没有亲眼经历过——没有就不许补间。
 */
import { Utils } from '../core/Utils';

type Formatter = (n: number) => string;

export interface NumOpts {
    /** 整值显示形式，默认 String。可含前后缀（如 `体力 ${n}/30`），滚动期间整串重排 */
    fmt?: Formatter;
    /** 涨跌量显示形式，默认 String（只喂绝对值，正负号由本模块加） */
    deltaFmt?: Formatter;
    /** 是否飘字，默认 true。密集网格（8 格属性条）里关掉，避免同帧多个飘字互相压 */
    float?: boolean;
    /** 是否脉冲，默认 true */
    pulse?: boolean;
    /**
     * 大额到账强演出（2026-10-07 正反馈批）：滚动拉长到 --dur-loop 档、飘字加大飘得更久、
     * 宿主弹跳强调（num-pop）。脉冲被弹跳覆盖（同元素 animation 类互斥）故不叠加。
     * 是否 strong 由调用方按量级判定（顶栏金钱在 ui.ts 顶栏闭包里算 delta）。
     */
    strong?: boolean;
}

/** 上次【已播放过动画】的值 + 它属于哪一局。缺席或跨局 = 首次写入（静默落定，不放动效） */
interface Baseline { epoch: number; value: number; }
const baselines = new WeakMap<HTMLElement, Baseline>();
/** 在跑的滚动 rAF，用于同一元素被连续改写时取消上一次 */
const running = new WeakMap<HTMLElement, number>();
/** 承载数字的专用文本节点：飘字是元素的子节点，滚动改写必须只动这个文本节点 */
const textNodes = new WeakMap<HTMLElement, Text>();

/**
 * 局号。面板是进程内单例，「再来一局」不换节点也不重建面板，
 * 所以基线必须按局作废——否则新开局会看到体力从上一位角色的值滚过来。
 */
let epoch = 0;

const ROLL_MS = 450;          // 对齐 --dur-long
const STRONG_ROLL_MS = 900;   // 大额档拉长一档，对齐 --dur-loop
const FLOAT_MS = 720;         // 对齐 --dur-float
const STRONG_FLOAT_MS = 900;  // 对齐 --dur-loop

/**
 * 写数字。刻意不整体赋 el.textContent——那会连带删掉正在飘的 .delta-float 子节点，
 * 飘字活不过第二帧。首次（或宿主被外部整体重写过）走 textContent 建立文本节点，之后只改 nodeValue。
 */
function writeNum(el: HTMLElement, s: string): void {
    let tn = textNodes.get(el);
    if (!tn || tn.parentNode !== el) {
        el.textContent = s;
        textNodes.set(el, el.firstChild as Text);
    } else {
        tn.nodeValue = s;
    }
}

/** 写入一个带反馈的数字。见文件头四条约束。 */
export function setNum(el: HTMLElement, to: number, opts: NumOpts | Formatter = {}): void {
    const o: NumOpts = typeof opts === 'function' ? { fmt: opts } : opts;
    const fmt = o.fmt ?? String;
    const base = baselines.get(el);
    const prev = base && base.epoch === epoch ? base.value : undefined;

    if (prev === undefined) {
        // 首写（含跨局的「伪首写」）：静默落定，并就地收回上一局可能还在跑的动效
        stopRoll(el);
        el.querySelector('.delta-float')?.remove();
        el.classList.remove('num-up', 'num-down');
        baselines.set(el, { epoch, value: to });
        writeNum(el, fmt(to));
        return;
    }
    if (prev === to) {
        // 值没变：⛔ 打断在跑的补间（会把中间值留成终值）；但也没动画收尾时，
        // 必须核对文本==终值（2026-10-02 顶栏体力卡 90/真值 100 的修口）——
        // roll 被连发刷新 stopRoll 掐死在首帧前时，文本会永远停在动画起点，这里幂等补写落定。
        if (!running.has(el)) writeNum(el, fmt(to));
        return;
    }

    // 任何一次真值变化都接管显示：先掐掉在跑的补间，否则旧动画会把刚落下的真值又滚回去
    stopRoll(el);

    if (Utils.prefersReducedMotion()) {
        writeNum(el, fmt(to));   // 减弱动效：就地落定，不欠一段「回头补播」
        baselines.set(el, { epoch, value: to });
        return;
    }
    // 看不见：文字照样落到真值，但基线留着，等下一次可见刷新回放整段涨跌
    if (offStage(el)) { writeNum(el, fmt(to)); return; }

    baselines.set(el, { epoch, value: to });
    // 页面在后台 ⇒ rAF 根本不会跑，补间会被无限期挂在队列里、文本停在起点
    // （2026-10-02 同案第二口）：直接静默落定，回来只见终值，⛔ 欠动画。
    if (document.hidden) { writeNum(el, fmt(to)); return; }
    // 不在此处先写终值：roll 首帧从旧值起步，否则会在补间开始前闪一帧答案
    roll(el, prev, to, fmt, o.strong ? STRONG_ROLL_MS : ROLL_MS);
    if (o.strong) {
        // 弹跳强调与 .num-up/.num-down 都吃 animation，同元素互相覆盖：strong 时不再叠脉冲
        el.classList.remove('num-strong');
        void el.offsetWidth;   // 同帧重触发需强制重排才能从头播（与 pulse 同法）
        el.classList.add('num-strong');
        window.setTimeout(() => el.classList.remove('num-strong'), STRONG_ROLL_MS + 60);
    } else if (o.pulse !== false) {
        pulse(el, to > prev);
    }
    if (o.float !== false) floatDelta(el, to - prev, o.deltaFmt ?? String, o.strong === true);
}

/**
 * 开新局时调用：作废全部基线。面板与宿主节点跨局复用，不 bump 局号就会让新开局
 * 从上一位角色的数值滚过来。在飞的滚动/飘字由该宿主下一次 setNum 的首写分支就地收回。
 */
export function resetFeedback(): void {
    epoch++;
}

/** 取消该宿主在跑的滚动 */
function stopRoll(el: HTMLElement): void {
    const raf = running.get(el);
    if (raf !== undefined) {
        cancelAnimationFrame(raf);
        running.delete(el);
    }
}

/** 此刻看不见 → 把动画推迟到下一次可见刷新 */
function offStage(el: HTMLElement): boolean {
    if (el.getClientRects().length === 0) return true;                       // 所在面板 display:none
    return !!document.querySelector('.month-progress-root:not(.hidden-panel)'); // 被月度执行弹窗压住
}

function roll(el: HTMLElement, from: number, to: number, fmt: Formatter, ms: number): void {
    const t0 = performance.now();
    const step = (now: number): void => {
        const k = Math.min(1, (now - t0) / ms);
        const eased = 1 - (1 - k) ** 3;   // easeOutCubic
        writeNum(el, fmt(Math.round(from + (to - from) * eased)));
        if (k < 1) running.set(el, requestAnimationFrame(step));
        else running.delete(el);
    };
    running.set(el, requestAnimationFrame(step));
}

function pulse(el: HTMLElement, up: boolean): void {
    el.classList.remove('num-up', 'num-down');
    // 同一帧内二次触发时类还在身上，强制重排才能让动画重头播（与 app.ts 角标 bump 同法）
    void el.offsetWidth;
    el.classList.add(up ? 'num-up' : 'num-down');
    window.setTimeout(() => el.classList.remove('num-up', 'num-down'), 460);
}

function floatDelta(el: HTMLElement, delta: number, fmt: Formatter, strong = false): void {
    el.querySelector('.delta-float')?.remove();
    const up = delta > 0;
    const chip = document.createElement('span');
    chip.className = `delta-float ${up ? 'up' : 'down'}${strong ? ' strong' : ''}`;
    chip.textContent = `${up ? '+' : '-'}${fmt(Math.abs(delta))}`;
    el.appendChild(chip);
    window.setTimeout(() => chip.remove(), (strong ? STRONG_FLOAT_MS : FLOAT_MS) + 60);
}
